import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Megaphone, ArrowLeft } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#263562]/15 rounded-lg px-4 py-2.5 text-sm text-[#1E2A4A] placeholder:text-[#949CA1] outline-none focus:border-[#3AB0E1] focus:ring-1 focus:ring-[#3AB0E1]/30 transition-all";
const labelCls =
  "block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-1.5";

const EMPTY = { title: "", body: "", link: "", active: true };

export default function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/announcements").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, link: form.link.trim() || null };
      if (editing === "new") {
        await api.post("/admin/announcements", payload);
        toast.success("Announcement published");
      } else {
        await api.put(`/admin/announcements/${editing}`, payload);
        toast.success("Announcement updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (a) => {
    try {
      await api.put(`/admin/announcements/${a.id}`, { title: a.title, body: a.body, link: a.link, active: !a.active });
      toast.success(a.active ? "Announcement hidden" : "Announcement is now live");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete announcement "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/announcements/${a.id}`);
      toast.success("Announcement deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (editing) {
    return (
      <div data-testid="announcement-editor" className="bg-white rounded-2xl border border-[#263562]/10 p-8 shadow-sm max-w-2xl">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display font-semibold text-2xl text-[#263562]">
            {editing === "new" ? "New Announcement" : "Edit Announcement"}
          </h2>
          <button onClick={() => setEditing(null)} className="flex items-center gap-2 text-sm text-[#5A6B85] hover:text-[#263562] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
        <label className={labelCls}>Title *</label>
        <input data-testid="announcement-editor-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. HostPoint POS 2.0 is live" className={inputCls} />
        <label className={`${labelCls} mt-5`}>Body</label>
        <textarea data-testid="announcement-editor-body" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="A short message shown to website visitors…" className={`${inputCls} resize-y`} />
        <label className={`${labelCls} mt-5`}>Link (optional)</label>
        <input data-testid="announcement-editor-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" className={inputCls} />
        <label className="mt-5 flex items-center gap-3 cursor-pointer">
          <input
            data-testid="announcement-editor-active"
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="w-4 h-4 accent-[#263562]"
          />
          <span className="text-sm text-[#263562]">Live on website</span>
        </label>
        <div className="mt-8 flex items-center gap-4">
          <button
            data-testid="announcement-editor-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] disabled:opacity-60 text-white font-semibold px-7 py-3 rounded-full transition-colors duration-300"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing === "new" ? "Publish" : "Save Changes"}
          </button>
          <button onClick={() => setEditing(null)} className="text-sm text-[#5A6B85] hover:text-[#263562] transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#5A6B85]">
          Active announcements appear as a card on the website's bottom-left corner
        </p>
        <button
          data-testid="admin-announcement-new"
          onClick={() => { setForm(EMPTY); setEditing("new"); }}
          className="flex items-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
        >
          <Plus className="w-4 h-4" /> New Announcement
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#3AB0E1]" />
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div
              key={a.id}
              data-testid={`admin-announcement-row-${a.id}`}
              className="bg-white rounded-xl border border-[#263562]/10 px-5 py-4 flex items-center gap-4"
            >
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${a.active ? "bg-[#3AB0E1]/15 text-[#1E8FBF]" : "bg-[#F5F8FC] text-[#949CA1]"}`}>
                <Megaphone className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#263562]">{a.title}</p>
                <p className="text-xs text-[#949CA1] truncate">
                  {a.active ? "Live" : "Hidden"} · {a.body || "No body text"}
                </p>
              </div>
              <button
                data-testid={`admin-announcement-toggle-${a.id}`}
                onClick={() => toggle(a)}
                className={`text-xs font-medium rounded-full px-4 py-2 border transition-colors ${a.active ? "border-[#3AB0E1]/50 text-[#1E8FBF] hover:bg-[#3AB0E1]/10" : "border-[#263562]/15 text-[#5A6B85] hover:border-[#3AB0E1]"}`}
              >
                {a.active ? "Hide" : "Go Live"}
              </button>
              <button
                data-testid={`admin-announcement-edit-${a.id}`}
                onClick={() => { setForm({ title: a.title, body: a.body || "", link: a.link || "", active: a.active }); setEditing(a.id); }}
                className="flex items-center gap-1.5 text-xs font-medium text-[#263562] border border-[#263562]/15 hover:border-[#3AB0E1] rounded-full px-4 py-2 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                data-testid={`admin-announcement-delete-${a.id}`}
                onClick={() => remove(a)}
                className="flex items-center gap-1.5 text-xs font-medium text-red-500 border border-red-200 hover:bg-red-50 rounded-full px-4 py-2 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-center text-sm text-[#949CA1] py-12">No announcements yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
